import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BackLink, PageHeader } from '@/layouts/AppLayout';

const schema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  city: z.string().min(1, 'City is required').max(100),
  state: z.string().max(100).optional(),
  country: z.string().max(100).optional(),
  address: z.string().optional(),
  contactPerson: z.string().max(255).optional(),
  contactEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  contactPhone: z.string().max(50).optional(),
  retainerStatus: z.enum(['active', 'paused', 'terminated']).optional(),
  retainerAmount: z.coerce.number().min(0).optional(),
  defaultCommissionPct: z.coerce.number().min(0).max(100).optional(),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export function CreateHotelPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { country: 'India', retainerStatus: 'active' },
  });

  const retainerStatus = watch('retainerStatus');

  const mutation = useMutation({
    mutationFn: (data: FormValues) => api.post('/hotels', data),
    onSuccess: (created: { id: string }) => {
      void qc.invalidateQueries({ queryKey: ['hotels'] });
      toast.success('Hotel created');
      navigate(`/hotels/${created.id}`);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to create hotel';
      setError('root', { message: msg });
    },
  });

  return (
    <div>
      <PageHeader title="New hotel" />
      <div className="p-6 max-w-2xl">
        <BackLink to="/hotels" label="Back to hotels" />
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Basic information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">Name *</Label>
                <Input id="name" {...register('name')} placeholder="Blackrock Resort" />
                {errors.name && <p className="text-xs text-danger-700">{errors.name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="notes">Notes</Label>
                <Input id="notes" {...register('notes')} placeholder="Any remarks…" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Location</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="city">City *</Label>
                  <Input id="city" {...register('city')} />
                  {errors.city && <p className="text-xs text-danger-700">{errors.city.message}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="state">State</Label>
                  <Input id="state" {...register('state')} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="country">Country</Label>
                  <Input id="country" {...register('country')} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="address">Address</Label>
                <Input id="address" {...register('address')} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Contact details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="contactPerson">Contact person</Label>
                <Input id="contactPerson" {...register('contactPerson')} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="contactEmail">Email</Label>
                  <Input id="contactEmail" type="email" {...register('contactEmail')} />
                  {errors.contactEmail && <p className="text-xs text-danger-700">{errors.contactEmail.message}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contactPhone">Phone</Label>
                  <Input id="contactPhone" {...register('contactPhone')} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Retainer & commission</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Retainer status</Label>
                  <Select
                    value={retainerStatus}
                    onValueChange={(v) => setValue('retainerStatus', v as FormValues['retainerStatus'], { shouldDirty: true })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="terminated">Terminated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="retainerAmount">Retainer amount (₹)</Label>
                  <Input id="retainerAmount" type="number" min={0} step="0.01" {...register('retainerAmount')} />
                  {errors.retainerAmount && <p className="text-xs text-danger-700">{errors.retainerAmount.message}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="defaultCommissionPct">Commission %</Label>
                  <Input id="defaultCommissionPct" type="number" min={0} max={100} step="0.01" {...register('defaultCommissionPct')} />
                  {errors.defaultCommissionPct && <p className="text-xs text-danger-700">{errors.defaultCommissionPct.message}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {errors.root && (
            <p className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{errors.root.message}</p>
          )}
          <div className="flex gap-3">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create hotel'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/hotels')}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
